// @ts-strict-ignore
import React, { useRef } from 'react';
import { Point, Series } from 'highcharts/highstock';
import { CustomLegend, CustomPoint } from './pieChart.helpers';
import { useTheme } from '@material-ui/core';

const getPointColor = (point: { color?: unknown }) => (typeof point.color === 'string' ? point.color : '');

const buildLegendMainRow = (name: string, color: string, fontSize = 14) => `
  <div style="display: flex; align-items: center; font-size: ${fontSize}px;">
    <div style="width: 4px; height: 16px; background-color: ${color};"></div>
    <div style="margin-left: 8px;">${name}</div>
  </div>
`;

const buildLegendPercentRow = (percent: string, fontSize = 24, fontWeight = 500) => `
  <div style="margin-top: 4px; margin-left: 12px; font-size: ${fontSize}px; font-weight: ${fontWeight};">${percent}%</div>
`;

const buildBigLegendMarkup = (point: Point, percent: string) =>
  `${buildLegendMainRow(point.name ?? '', getPointColor(point))}${buildLegendPercentRow(percent)}`;

const buildCompactLegendMarkup = (point: Point, percent: string) => `
  ${buildLegendPercentRow(percent, 12, 400)}
  ${buildLegendMainRow(point.name ?? '', getPointColor(point), 12)}
`;

const buildTooltipMarkup = (point: { color?: unknown; name?: string }, percent: string) => `
  <div style="display: flex; align-items: center;">
    <div style="width: 4px; height: 12px; background-color: ${getPointColor(point)};"></div>
    <div style="margin-left: 4px;">${point.name ?? ''}</div>
  </div>
  <div style="margin-left: 8px; font-size: 24px; font-weight: 500;">${percent}%</div>
`;

export const usePieChartDefaultOptions = (
  dataLength: number,
  chartWidth: number | string | null,
  chartHeight: number | null,
  hideTooltip = false,
  hideLegend = false,
  bigLegend = false,
  onPointHover?: (color: string, value: number) => void,
  tooltipVisibility?: React.MutableRefObject<boolean>,
): Highcharts.Options => {
  const internalTooltipVisibility = useRef<boolean>(false);
  const tooltipVisibilityRef = tooltipVisibility ?? internalTooltipVisibility;
  const hoverRestoreTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const theme = useTheme();
  const legendHoverBoundAttribute = 'data-pie-legend-hover-bound';
  const formatPercent = (value?: number | null) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return '0.00';
    return value.toFixed(2);
  };
  return {
    responsive: {
      rules: [{
        condition: {
          maxWidth: 767
        },
        chartOptions: {
          legend: {
            enabled: !hideLegend,
            itemStyle: {
              fontSize: '12px',
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            labelFormatter() {
              const point = this as unknown as Point;
              const percent = formatPercent(point.y);
              if (bigLegend) {
                return buildBigLegendMarkup(point, percent);
              }
              return buildCompactLegendMarkup(point, percent);
            },
          },
        },
      }],
    },
    accessibility: {
      enabled: false,
    },
    title: {
      text: '',
    },
    credits: {
      enabled: false,
    },
    chart: {
      width: chartWidth || null,
      type: 'pie',
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      height: chartHeight || 256,
      spacingTop: 0,
      spacingBottom: 0,
      spacingLeft: 0,
      events: {
        load() {
          const legend: CustomLegend = this.legend;
          this.update({
            legend: {
                itemWidth: legend.legendWidth,
            },
          });
        },
        render() {
          const series: Series = this.series[0];
          if (!series) {
            return;
          }
          if (!hideTooltip) {
            series.points.forEach((point: CustomPoint) => {
              const legendLabelElement = point.legendItem?.label?.element;
              if (!legendLabelElement || legendLabelElement.getAttribute(legendHoverBoundAttribute) === 'true') {
                return;
              }

              legendLabelElement.setAttribute(legendHoverBoundAttribute, 'true');
              point.legendItem?.label
                .on('mouseenter', () => {
                  tooltipVisibilityRef.current = true;
                  point.onMouseOver();
                })
                .on('mouseleave', () => {
                  if (hoverRestoreTimeoutRef.current !== null) {
                    clearTimeout(hoverRestoreTimeoutRef.current);
                    hoverRestoreTimeoutRef.current = null;
                  }
                  series.chart.tooltip.hide(0);
                  tooltipVisibilityRef.current = false;
                });
            });
          }
        },
      },
      zooming: {
        mouseWheel: {
          enabled: false,
        },
      },
    },
    plotOptions: {
      pie: {
        stickyTracking: false,
        dataLabels: {
          enabled: false,
        },
        borderWidth: 0,
        showInLegend: true,
        ...(dataLength > 1 && {
          borderRadius: 8,
        }),
        point: {
          events: {
            mouseOut() {
              const willKeepHover = tooltipVisibilityRef.current;
              const point = this;
              if (willKeepHover) {
                if (hoverRestoreTimeoutRef.current !== null) {
                  clearTimeout(hoverRestoreTimeoutRef.current);
                }
                hoverRestoreTimeoutRef.current = setTimeout(() => {
                  hoverRestoreTimeoutRef.current = null;
                  point.setState('hover');
                  point.series.chart.tooltip.refresh(point);
                }, 0);
              } else {
                if (hoverRestoreTimeoutRef.current !== null) {
                  clearTimeout(hoverRestoreTimeoutRef.current);
                  hoverRestoreTimeoutRef.current = null;
                }
                this.series.chart.tooltip.hide(0);
                onPointHover?.('', 0);
              }
            },
            mouseOver() {
              const colorString = typeof this.color === 'string' ? this.color : '';
              onPointHover?.(colorString, this.y ?? 0);
            },
          },
        },
      },
      column: {
        stacking: 'percent',
      },
    },
    tooltip: {
      enabled: true,
      hideDelay: 0,
      useHTML: true,
      borderWidth: 0,
      backgroundColor: 'none',
      headerFormat: '',
      shadow: false,
      style: {
        fontSize: '12px',
        textAlign: 'left',
        color: theme.color.greys[60],
        whiteSpace: 'normal',

      },
      formatter: function() {
        if (!hideTooltip) {
          return buildTooltipMarkup(this, formatPercent(this.y));
        }
      },
      positioner(labelWidth: number, labelHeight: number) {
        return {
          x: (this.chart.plotWidth - labelWidth - 10) / 2,
          y: (this.chart.plotHeight - labelHeight - 10) / 2,
        };
      },
    },
    legend: {
      enabled: !hideLegend,
      useHTML: true,
      floating: false,
      symbolWidth: 0,
      symbolHeight: 0,
      symbolPadding: 0,
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'middle',
      width: '40%',
      navigation: {
        enabled: false,
      },
      itemStyle: {
        color: theme.color.greys[60],
        fontSize: '14px',
        whiteSpace: 'normal',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      itemHoverStyle: {
        color: theme.color.greys[60],
      },
      events: {
        itemClick: function () {
          return false;
        },
      },
      itemDistance: 10,
      itemMarginBottom: 10,
      labelFormatter() {
        const point = this as unknown as Point;
        return buildBigLegendMarkup(point, formatPercent(point.y));
      },
    },
  };
};
